import {
  MeshBasicMaterial,
  Mesh,
  PlaneGeometry,
  ShaderMaterial,
  Object3D,
  Vector4,
  Vector2,
  BackSide,
  Color,
  ShaderChunk,
} from "three";
import { VBase } from "./VBase";
import VUtil from "../VUtils";

const defaultOptions = {
  textSize: 0.05,
  textColor: 0x000000,
  iconWidth: 0.05,
  iconHeight: 0.05,
  needColorText: true,
  width: 0.6,
  height: 0.6,
  disabledColor: 0xc8c8c8,
  defaultColor: 0x000000,
  orientation: "right",
};

/*
  使用案例

    const colorPicker = this.uiLib.createColorPicker({
        iconWidth: 0.1,
        iconHeight: 0.1,
        orientation: "top",
        needColorText: true,
        width: 0.5,
        height: 0.5,
        textColor: 0xffffff,
      });
      colorPicker.addOnValueChangeEvent((color) => {
        console.log("选择了颜色：", color);
      });
      this.uiLib.addUIToScene(colorPicker);


*/
export class VColorPicker extends VBase {
  /**
   * options:
   * {
   *      priority: 优先级，默认为0，值越小越靠前
   *      renderOrder: 渲染顺序，默认为99999
   *      textSize: 文字大小，默认为 0.03
   *      iconWidth: 拾色器小图标的宽度，默认为0.05
   *      iconHeight: 拾色器小图标的高度，默认为0.05
   *      defaultColor: 初始的默认颜色，默认为0xffffff
   *      textColor: 文字颜色，默认为 0x000000
   *      needColorText: 是否需要显示颜色代码（十六进制，如"0xffffff"），布尔值,默认为true
   *      width: 拾色器宽度，默认为 0.6
   *      height: 拾色器高度，默认为 0.6
   *      disabledColor: ui对象禁用颜色，默认为 0xC8C8C8
   *      orientation：拾色器出现在图标的哪个方向，"top"/"button"/"left"/"right" ,默认为"right"
   * }
   * @param {*} options
   */
  constructor(options, vuiLib) {
    if (!options) options = {};

    const tempOptions = {
      priority: options.priority !== undefined ? options.priority : 0,
      renderOrder:
        options.renderOrder !== undefined ? options.renderOrder : 99999,
      textSize:
        options.textSize !== undefined
          ? options.textSize
          : defaultOptions.textSize,
      color:
        options.defaultColor !== undefined
          ? options.defaultColor
          : defaultOptions.defaultColor,
      textColor:
        options.textColor !== undefined
          ? options.textColor
          : defaultOptions.textColor,
      width:
        options.iconWidth !== undefined
          ? options.iconWidth
          : defaultOptions.iconWidth,
      height:
        options.iconHeight !== undefined
          ? options.iconHeight
          : defaultOptions.iconHeight,
      width2:
        options.width !== undefined ? options.width : defaultOptions.width,
      height2:
        options.height !== undefined ? options.height : defaultOptions.height,
      orientation: options.orientation
        ? options.orientation
        : defaultOptions.orientation,
      needColorText:
        options.needColorText !== undefined
          ? options.needColorText
          : defaultOptions.needColorText,
      onValueChange: options.onValueChange ? options.onValueChange : null,
      disabledColor:
        options.disabledColor !== undefined
          ? options.disabledColor
          : defaultOptions.disabledColor,
    };

    super(tempOptions, vuiLib);
    this._vType = "VColorPicker";

    this._otherProps = {
      width: tempOptions.width2,
      height: tempOptions.height2,
      orientation: tempOptions.orientation,
      needColorText: tempOptions.needColorText,
      onValueChangeEvent: [],
    };

    this.name = "VColorPicker";

    this.width = this._otherProps.width;
    this.height = this._otherProps.height;

    // 创建文字
    this._createText();

    // 创建拾色器
    this.saturationParams = {
      segment: 1,
      targetColor: new Vector4(1, 0, 0, 1),
      maskColor: new Vector4(1, 1, 1, 1),
      maskOffset: 0.015,
      maskPos: new Vector2(-0.5, 0.5),
    };
    this.hueParams = {
      segment: 1.0,
      width: this._otherProps.width / 10,
      height: this._otherProps.height,
      hues: [2, 0, 1, 2, 0, 1],
      colors: [
        new Vector4(1, 0, 0, 1),
        new Vector4(0, 0, 1, 1),
        new Vector4(0, 0, 1, 1),
        new Vector4(0, 1, 0, 1),
        new Vector4(0, 1, 0, 1),
        new Vector4(1, 0, 0, 1),
      ],
      maskColor: new Vector4(1, 1, 1, 1),
      maskOffset: 0.01,
    };
    this._createColorPicker();
  }

  /**
   * 创建颜色文字 内部使用
   */
  _createText() {
    if (!this._otherProps.needColorText) return;
    if (this.text) {
      this.text.geometry.dispose();
      if (Array.isArray(this.text.material)) {
        this.text.material.forEach((m) => {
          if (m.dispose) m.dispose();
        });
      } else this.text.material.dispose();
      this.text.removeFromParent();
      this.text = null;
    }

    let color = this.material.color.getHex().toString(16);
    if (color === "0") color = "000000";

    // 文字
    this.text = VUtil.createText(
      "0x" + color,
      this._textProps.textSize,
      this._textProps.textColor,
      "normal",
      (tex) => {
        tex.position.x += tex._width / 2 + this._baseProps.width;

        this.width += tex._width + this._baseProps.width;
      }
    );

    this.text.renderOrder = this._baseProps.renderOrder;
    this.add(this.text);
  }

  onSelectEnd(obj) {
    if (!this._baseProps || !this._baseProps.enabled) return;
    this.colorPicker.visible = !this.colorPicker.visible;

    super.onSelectEnd();
  }

  getClickable() {
    if (!this._baseProps.enabled) return [];
    let res = [];
    if (!this.visible) return [];
    res.push(this);
    if (this.colorPicker.visible) {
      res.push(
        this.colorPicker._Saturation,
        this.colorPicker._Hue,
        this.colorPicker._mask
      );
    }
    return res;
  }

  /**
   * 创建调色板 内部使用
   */
  _createColorPicker() {
    this.colorPicker = new Object3D();
    this.colorPicker.name = "ColorPicker";
    this.colorPicker.visible = false;

    let Saturation = this._createSaturation();
    this.colorPicker.attach(Saturation);
    this.colorPicker._Saturation = Saturation;
    if (this.vuilib) {
      this.vuilib.addComponents(Saturation, this._priority);
    }

    let Hue = this._createHue();
    this.colorPicker.attach(Hue);
    this.colorPicker._Hue = Hue;
    if (this.vuilib) {
      this.vuilib.addComponents(Hue, this._priority);
    }

    this.add(this.colorPicker);

    let mask = new Mesh(
      new PlaneGeometry(10, 10),
      new MeshBasicMaterial({
        transparent: true,
        opacity: 0.01,
        depthWrite: false,
      })
    );
    mask.position.z = -0.001;
    mask.renderOrder = this._baseProps.renderOrder;
    mask.onVRClick = () => {
      this.colorPicker.visible = false;
    };
    this.colorPicker.add(mask);
    this.colorPicker._mask = mask;
    if (this.vuilib) {
      this.vuilib.addComponents(mask, this._priority);
    }

    switch (this._otherProps.orientation) {
      case "top": {
        //"top"/"button"/"left"/"right"
        this.colorPicker.position.y =
          this._otherProps.height / 2 + this._baseProps.height;
        break;
      }
      case "button": {
        this.colorPicker.position.y =
          -this._otherProps.height / 2 - this._baseProps.height;
        break;
      }
      case "left": {
        this.colorPicker.position.x =
          -this._otherProps.width / 2 -
          this._baseProps.iconWidth * 2 -
          this._otherProps.width / 10;
        break;
      }
      default: {
        this.colorPicker.position.x =
          this._otherProps.width / 2 + this._baseProps.width;
        if (this.text) {
          if (this.text._width)
            this.colorPicker.position.x +=
              this.text._width + this._baseProps.width;
          else {
            const setWidthInterval = setInterval(() => {
              if (this.text._width) {
                this.colorPicker.position.x +=
                  this.text._width + this._baseProps.width;
                clearInterval(setWidthInterval);
              }
            }, 100);
          }
        }
        break;
      }
    }
  }

  /**
   * 创建Saturation 内部使用
   * @returns
   */
  _createSaturation() {
    let planeGeo = new PlaneGeometry(
      this.saturationParams.segment,
      this.saturationParams.segment
    );
    let planeMat = new ShaderMaterial({
      uniforms: {
        targetColor: { value: this.saturationParams.targetColor },
        resolution: { value: this.saturationParams.segment },
        maskPos: { value: this.saturationParams.maskPos },
        maskColor: { value: this.saturationParams.maskColor },
        maskOffset: { value: this.saturationParams.maskOffset },
      },
      side: BackSide,
      transparent: true,
      depthTest: this.depthTest,
      vertexShader: `
        ${ShaderChunk.common}
        ${ShaderChunk.logdepthbuf_pars_vertex}
        varying vec3 modelPos;
        void main() {
          modelPos = position;
          gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
          ${ShaderChunk.logdepthbuf_vertex}
        }
      `,
      fragmentShader: `
        ${ShaderChunk.common}
        ${ShaderChunk.logdepthbuf_pars_fragment}
        uniform vec4 targetColor;
        uniform float resolution;
        uniform float maskOffset;
        uniform vec4 maskColor;
        uniform vec2 maskPos;
        varying vec3 modelPos;
        vec4 GetSaturation(vec4 targetC, float x, float y)
        {
          vec4 newColor = vec4(1.0,1.0,1.0,1.0);
          newColor.r = (targetC.r < 0.999) ? ((1.0 - targetC.r) * (1.0 - x) + targetC.r) : 1.0;
          newColor.g = (targetC.g < 0.999) ? ((1.0 - targetC.g) * (1.0 - x) + targetC.g) : 1.0;
          newColor.b = (targetC.b < 0.999) ? ((1.0 - targetC.b) * (1.0 - x) + targetC.b) : 1.0;
          newColor *= (1.0 - y);
          newColor.a = 1.0;
          return newColor;
        }

        void main() {
          if(modelPos.x >= maskPos.x-maskOffset && modelPos.x <= maskPos.x+maskOffset && modelPos.y >= maskPos.y-maskOffset && modelPos.y <= maskPos.y+maskOffset)
            gl_FragColor = maskColor;
          else{
            float x = (modelPos.x + resolution/2.0)/resolution;
            float y = (modelPos.y + resolution/2.0)/resolution;
            gl_FragColor = GetSaturation(targetColor,x,y);
            ${ShaderChunk.logdepthbuf_fragment}
          }
        }
      `,
    });

    let plane = new Mesh(planeGeo, planeMat);
    plane.renderOrder = this._baseProps.renderOrder * 3;
    plane.rotateX(Math.PI);
    plane.scale.set(
      this._otherProps.width / this.saturationParams.segment,
      this._otherProps.height / this.saturationParams.segment,
      1
    );
    plane._type = "Saturation";
    plane.name = "ColorPicker_Saturation";

    // 注册光标进入事件
    plane.onEcsyHoverEnter = () => {};
    // 注册光标退出事件
    plane.onEcsyHoverExit = () => {};
    // 注册点击事件
    plane.onVRClick = (obj, clickObj) => {
      this._getColor(clickObj.point);
    };

    return plane;
  }

  /**
   * 创建hue 内部使用
   * @returns
   */
  _createHue() {
    let planeGeo = new PlaneGeometry(
      this.hueParams.width,
      this.hueParams.height,
      this.hueParams.segment,
      this.hueParams.segment
    );
    let planeMat = new ShaderMaterial({
      uniforms: {
        resolution: { value: this.hueParams.height },
        hues: { value: this.hueParams.hues },
        maskHeight: { value: this.hueParams.height / 2.0 },
        colors: { value: this.hueParams.colors },
        maskColor: { value: this.hueParams.maskColor },
        maskOffset: { value: this.hueParams.maskOffset },
      },
      side: BackSide,
      transparent: true,
      depthTest: this.depthTest,
      vertexShader: `
        ${ShaderChunk.common}
        ${ShaderChunk.logdepthbuf_pars_vertex}
        varying vec3 modelPos;
        void main() {
          modelPos = position;
          gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
          ${ShaderChunk.logdepthbuf_vertex}
        }
      `,
      fragmentShader: `
        ${ShaderChunk.common}
        ${ShaderChunk.logdepthbuf_pars_fragment}
        uniform float resolution;
        uniform float maskHeight;
        uniform int[6] hues;
        uniform vec4[6] colors;
        varying vec3 modelPos;
        float c = 0.166667;
        uniform float maskOffset;
        uniform vec4 maskColor;
        vec4 GetHue(float y)
        {
          y = clamp(y,0.0,1.0);
          highp int index = int(floor(y/c));
          int h = hues[index];
          vec4 newColor = colors[index];
          float less = (y-float(index)*c)/c;
          newColor[h] = index % 2 == 0 ? less : 1.0-less;
          return newColor;
        }
        void main() {
          if(modelPos.y > maskHeight - maskOffset && modelPos.y<maskHeight + maskOffset)
            gl_FragColor = maskColor;
          else{
            float y = (modelPos.y + resolution/2.0)/resolution;
            gl_FragColor = GetHue(y);
          }
          ${ShaderChunk.logdepthbuf_fragment}
        }
      `,
    });
    let plane = new Mesh(planeGeo, planeMat);
    plane.renderOrder = this._baseProps.renderOrder * 3;
    plane.rotateX(Math.PI);
    plane.rotateZ(Math.PI);
    plane.scale.set(
      1 / this.hueParams.segment,
      1 / this.hueParams.segment,
      1 / this.hueParams.segment
    );
    plane.position.x += this._otherProps.width / 2 + this.hueParams.width;
    plane._type = "Hue";
    plane.name = "ColorPicker_Hue";
    // 注册光标进入事件
    plane.onEcsyHoverEnter = () => {};
    // 注册光标退出事件
    plane.onEcsyHoverExit = () => {};
    // 注册点击事件
    plane.onVRClick = (obj, clickObj) => {
      this._changeHue(clickObj.point);
    };
    return plane;
  }

  /**
   * 获取点击颜色 内部使用
   * @param {*} point 射线检测到的世界坐标
   * @returns
   */
  _getColor(point) {
    let localPos = this.colorPicker._Saturation.worldToLocal(point.clone());

    this._selectColor(localPos);
  }

  /**
   *  内部使用
   * @param {*} point
   */
  _selectColor(point) {
    this.colorPicker._Saturation.material.uniforms.maskPos.value = new Vector2(
      point.x,
      point.y
    );
    this.saturationParams.maskPos = new Vector2(point.x, point.y);
    var x =
      (point.x + this.saturationParams.segment / 2) /
      this.saturationParams.segment;
    var y =
      (point.y + this.saturationParams.segment / 2) /
      this.saturationParams.segment;

    const newColor = this._getSaturationColor(
      [
        this.saturationParams.targetColor.x,
        this.saturationParams.targetColor.y,
        this.saturationParams.targetColor.z,
      ],
      x,
      y
    );

    this.material.color = newColor;
    VUtil.convertSRGBToLinear(this.material);

    if (this._otherProps.needColorText) {
      this._createText();
    }

    let colorStr = newColor.getHex().toString(16);
    if (colorStr === "0") colorStr = "000000";
    for (var i = 0; i < this._otherProps.onValueChangeEvent.length; i++) {
      this._otherProps.onValueChangeEvent[i]("#" + colorStr);
    }
  }

  /**
   * 从外部设置当前颜色
   * @param {*} color Color对象或颜色字符串（"#ffffff"、0Xffffff）
   */
  setColor(color) {
    if (color.isColor) this.material.color = color;
    else this.material.color = new Color(color);

    if (this._otherProps.needColorText) {
      this._createText();
    }
  }

  /**
   * 修改色条 内部使用
   * @param {*} point
   */
  _changeHue(point) {
    // 转到本地空间
    const localPos = this.colorPicker._Hue.worldToLocal(point.clone());

    this.colorPicker._Hue.material.uniforms.maskHeight.value = localPos.y;

    let y = (localPos.y + this.hueParams.height / 2) / this.hueParams.height;
    let color = this._getHueColor(y);

    this.colorPicker._Saturation.material.uniforms.targetColor.value = color;
    this.saturationParams.targetColor = color;

    this._selectColor(this.saturationParams.maskPos);
  }

  /**
   *  内部使用
   * @param {*} color
   * @param {*} x
   * @param {*} y
   * @returns
   */
  _getSaturationColor(color, x, y) {
    var newColor = [1, 1, 1];
    for (var i = 0; i < 3; i++) {
      if (color[i] !== 1) {
        newColor[i] = (1 - color[i]) * (1 - x) + color[i];
      }
    }

    for (var j = 0; j < 3; j++) {
      newColor[j] *= 1 - y;
    }

    return new Color(newColor[0], newColor[1], newColor[2], 1);
  }

  /**
   * 根据y值获取hue选中的颜色 内部使用
   * @param {*} y
   * @returns
   */
  _getHueColor(y) {
    if (y < 0) y = 0.0;
    if (y > 1.0) y = 1.0;
    var c = 0.166667;
    var index = Math.floor(y / c);
    var h = this.hueParams.hues[index];
    var newColor = this.hueParams.colors[index].clone();
    var less = (y - index * c) / c;
    var value = index % 2 === 0 ? less : 1.0 - less;
    if (h === 0) newColor.x = value;
    else if (h === 1) newColor.y = value;
    else if (h === 2) newColor.z = value;
    return newColor;
  }

  /**
   * 添加一个颜色修改回调事件，回调事件参数为 颜色值，如"#ffffff"
   * @param {*} fun
   * @returns
   */
  addOnValueChangeEvent(fun) {
    if (!fun) return;
    this._otherProps.onValueChangeEvent.push(fun);
  }

  /**
   * 移除一个颜色修改回调事件
   * @param {*} fun
   * @returns
   */
  removeOnValueChangeEvent(fun) {
    if (!fun) return;
    let index = this._otherProps.onValueChangeEvent.indexOf(fun);
    if (index >= 0) {
      this._otherProps.onValueChangeEvent.splice(index, 1);
    }
  }

  /**
   * 移除所有颜色修改回调事件
   */
  removeAllOnValueChangeEvent() {
    this._otherProps.onValueChangeEvent = [];
  }
}
